import { AlertBannerSeverity, AlertBannersData } from "../dashboardOverview.types";
import { AlertCircleIcon, CheckCircleIcon, WarningIcon } from "./DashboardIcons";
// ===== NEW FILE: top-of-page alert banners (copy this whole file) =====
import { JSX, useState } from "react";

export interface AlertBannersListProps {
	data: AlertBannersData;
	onAction?: (bannerId: string) => void;
	/** Called after a banner is dismissed (dismissal itself is handled locally). */
	onDismiss?: (bannerId: string) => void;
}

const toneClassBySeverity: Record<AlertBannerSeverity, string> = {
	error: "cd-alert-banner--error",
	warning: "cd-alert-banner--warning",
	success: "cd-alert-banner--success",
	info: "cd-alert-banner--info",
};

const iconBySeverity: Record<AlertBannerSeverity, JSX.Element> = {
	error: <AlertCircleIcon />,
	warning: <WarningIcon />,
	success: <CheckCircleIcon />,
	info: <CheckCircleIcon />,
};

/**
 * Full-width, dismissible banners for urgent, page-level items — an NSF
 * debit, the next payday's approval deadline, a remittance date, etc.
 * Distinct from the right-column "Alerts & Notifications" panel, which is
 * a running log rather than an action queue.
 *
 * Dismissal is handled locally (component state) since the mock data has
 * no backend to persist it against; `onDismiss` still fires so the parent
 * can react (analytics, etc.) if needed.
 */
export const AlertBannersList = ({ data, onAction, onDismiss }: AlertBannersListProps) => {
	const [dismissedIds, setDismissedIds] = useState<string[]>([]);

	const visibleBanners = data.banners.filter((banner) => !dismissedIds.includes(banner.id));

	if (visibleBanners.length === 0) {
		return null;
	}

	const handleDismiss = (bannerId: string) => {
		setDismissedIds((current) => [...current, bannerId]);
		onDismiss?.(bannerId);
	};

	return (
		<div className="cd-alert-banners">
			{visibleBanners.map((banner) => (
				<div className={`cd-alert-banner ${toneClassBySeverity[banner.severity]}`} key={banner.id}>
					<span className="cd-alert-banner__icon">{iconBySeverity[banner.severity]}</span>
					<div className="cd-alert-banner__body">
						<div className="cd-alert-banner__title">{banner.title}</div>
						<div className="cd-alert-banner__description">{banner.description}</div>
					</div>
					{banner.actionLabel && (
						<button type="button" className="cd-alert-banner__action" onClick={() => onAction?.(banner.id)}>
							{banner.actionLabel}
						</button>
					)}
					{banner.dismissible !== false && (
						<button
							type="button"
							className="cd-alert-banner__dismiss"
							aria-label="Dismiss"
							onClick={() => handleDismiss(banner.id)}
						>
							&times;
						</button>
					)}
				</div>
			))}
		</div>
	);
};
